import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import type { DailyReport } from '@/lib/api';

interface AiSummaryCardProps {
  report: DailyReport | null;
  isLoading?: boolean;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business ダッシュボードのAIインサイトカード。日次サマリーの見出し・本文・良化/懸念ポイントを表示
 * @graph-connects none
 */
export function AiSummaryCard({ report, isLoading }: AiSummaryCardProps) {
  if (isLoading) {
    return (
      <GlassCard className="p-6">
        <div className="skeleton-shimmer h-4 w-32 rounded mb-4" />
        <div className="space-y-2">
          <div className="skeleton-shimmer h-3 w-full rounded" />
          <div className="skeleton-shimmer h-3 w-5/6 rounded" />
          <div className="skeleton-shimmer h-3 w-4/6 rounded" />
        </div>
      </GlassCard>
    );
  }

  if (!report) {
    return (
      <GlassCard className="p-6">
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          レポートデータがありません
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding="none" className="ai-summary p-6">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-lg">{'✨'}</span>
        <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          AI インサイト
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {report.report_date}
        </span>
        <Badge variant={report.generated_by === 'gemini' ? 'info' : 'default'} size="sm">
          {report.generated_by === 'gemini' ? 'Gemini 生成' : 'ルールベース'}
        </Badge>
      </div>

      <p className="text-base font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
        {report.headline}
      </p>
      <p className="text-sm mb-4 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
        {report.body}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-success)' }}>
            良化ポイント
          </p>
          <ul className="space-y-1">
            {report.highlights.map((item) => (
              <li
                key={item}
                className="text-xs flex gap-2"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <span style={{ color: 'var(--color-success)' }}>▲</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-warning)' }}>
            注意・悪化ポイント
          </p>
          <ul className="space-y-1">
            {report.concerns.map((item) => (
              <li
                key={item}
                className="text-xs flex gap-2"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <span style={{ color: 'var(--color-warning)' }}>▼</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </GlassCard>
  );
}
